import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CODES = ["P", "A", "HD", "WO"];
const nextCode = (c) => {
  if (!c) return "P";
  const i = CODES.indexOf(c);
  return i === CODES.length - 1 ? "" : CODES[i + 1];
};

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Attendance() {
  const [month, setMonth] = useState(currentMonth());
  const [employees, setEmployees] = useState([]);
  const [dates, setDates] = useState([]);
  const [cells, setCells] = useState({}); // {empId_date: status}
  const [dirty, setDirty] = useState(new Set());
  const [adjust, setAdjust] = useState({}); // { empId: {paid_leave, ot_hours, incentive} }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, attRes, adjRes] = await Promise.all([
        api.get("/employees"),
        api.get(`/attendance/${month}`),
        api.get(`/monthly-adjust/${month}`),
      ]);
      setEmployees(empRes.data.filter((e) => e.active));
      setDates(attRes.data.dates);
      const map = {};
      attRes.data.cells.forEach((c) => (map[`${c.employee_id}_${c.date}`] = c.status));
      setCells(map);
      const am = {};
      adjRes.data.forEach((a) => (am[a.employee_id] = { paid_leave: a.paid_leave, ot_hours: a.ot_hours, incentive: a.incentive }));
      setAdjust(am);
      setDirty(new Set());
    } finally {
      setLoading(false);
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const clickCell = (empId, date) => {
    const k = `${empId}_${date}`;
    const cur = cells[k];
    const nx = nextCode(cur);
    setCells((c) => ({ ...c, [k]: nx }));
    setDirty((d) => new Set(d).add(k));
  };

  const summary = useMemo(() => {
    const s = {};
    employees.forEach((e) => (s[e.id] = { P: 0, A: 0, HD: 0, WO: 0 }));
    Object.entries(cells).forEach(([k, v]) => {
      const [empId] = k.split("_");
      if (!v || !s[empId]) return;
      s[empId][v] = (s[empId][v] || 0) + 1;
    });
    return s;
  }, [cells, employees]);

  const saveAll = async () => {
    setSaving(true);
    try {
      const cellsToSave = Array.from(dirty).map((k) => {
        const [employee_id, ...rest] = k.split("_");
        const date = rest.join("_");
        return { employee_id, date, status: cells[k] || "" };
      });
      if (cellsToSave.length) {
        await api.post("/attendance/bulk", { month, cells: cellsToSave });
      }
      // save monthly adjust
      await Promise.all(
        Object.entries(adjust).map(([employee_id, v]) =>
          api.post("/monthly-adjust", {
            month, employee_id,
            paid_leave: Number(v.paid_leave || 0),
            ot_hours: Number(v.ot_hours || 0),
            incentive: Number(v.incentive || 0),
          })
        )
      );
      toast.success("Attendance & adjustments saved");
      setDirty(new Set());
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const fillAll = (empId, code) => {
    setDates((cur) => {
      const upd = { ...cells };
      const d2 = new Set(dirty);
      cur.forEach((d) => {
        const k = `${empId}_${d}`;
        upd[k] = code;
        d2.add(k);
      });
      setCells(upd);
      setDirty(d2);
      return cur;
    });
  };

  return (
    <div className="p-8 space-y-5" data-testid="attendance-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold">Attendance</h1>
          <p className="text-sm text-slate-500 mt-1">Click a cell to cycle P → A → HD → WO → blank.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs text-slate-500 font-mono uppercase">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="att-month" />
          </div>
          <Button onClick={saveAll} disabled={saving} data-testid="att-save-btn">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {[
          ["P", "Present", "bg-emerald-100 text-emerald-800"],
          ["A", "Absent", "bg-rose-100 text-rose-800"],
          ["HD", "Half Day (0.5)", "bg-amber-100 text-amber-800"],
          ["WO", "Weekly Off", "bg-slate-200 text-slate-700"],
        ].map(([c, l, cl]) => (
          <span key={c} className={`px-2 py-1 rounded-md font-mono ${cl}`}>{c} — {l}</span>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : employees.length === 0 ? (
        <div className="p-10 text-center text-slate-500 border border-dashed rounded-lg">
          Add employees first from the Employees page.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="att-table min-w-full">
            <thead>
              <tr>
                <th className="sticky-col text-left" style={{ minWidth: 200 }}>Employee</th>
                {dates.map((d) => {
                  const day = Number(d.slice(-2));
                  const dow = new Date(d).toLocaleDateString("en-US", { weekday: "short" }).slice(0,1);
                  return <th key={d} className="text-center" title={d}>{day}<div className="text-[9px] opacity-60">{dow}</div></th>;
                })}
                <th>P</th><th>A</th><th>HD</th><th>WO</th>
                <th>Paid Leave</th><th>OT hrs</th><th>Incentive</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id}>
                  <td className="sticky-col">
                    <div className="font-body font-medium text-slate-900">{e.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2">
                      {e.employee_id}
                      <span className="opacity-50">•</span>
                      <button className="underline-offset-2 hover:underline" onClick={()=>fillAll(e.id,"P")} data-testid={`fill-P-${e.employee_id}`}>All P</button>
                      <button className="underline-offset-2 hover:underline" onClick={()=>fillAll(e.id,"WO")}>All WO</button>
                    </div>
                  </td>
                  {dates.map((d) => {
                    const k = `${e.id}_${d}`;
                    const s = cells[k] || "";
                    return (
                      <td key={d} onClick={() => clickCell(e.id, d)}
                          className={`att-cell ${s || "empty"}`}
                          data-testid={`cell-${e.employee_id}-${d.slice(-2)}`}>
                        {s || "·"}
                      </td>
                    );
                  })}
                  <td className="text-center num">{summary[e.id]?.P || 0}</td>
                  <td className="text-center num">{summary[e.id]?.A || 0}</td>
                  <td className="text-center num">{summary[e.id]?.HD || 0}</td>
                  <td className="text-center num">{summary[e.id]?.WO || 0}</td>
                  <td>
                    <Input
                      type="number" step="0.5"
                      value={adjust[e.id]?.paid_leave ?? 0}
                      onChange={(ev)=>setAdjust((a)=>({ ...a, [e.id]: { ...(a[e.id]||{}), paid_leave: ev.target.value } }))}
                      className="h-7 w-16 text-right font-mono"
                    />
                  </td>
                  <td>
                    <Input
                      type="number" step="0.5"
                      value={adjust[e.id]?.ot_hours ?? 0}
                      onChange={(ev)=>setAdjust((a)=>({ ...a, [e.id]: { ...(a[e.id]||{}), ot_hours: ev.target.value } }))}
                      className="h-7 w-16 text-right font-mono"
                    />
                  </td>
                  <td>
                    <Input
                      type="number" step="0.01"
                      value={adjust[e.id]?.incentive ?? 0}
                      onChange={(ev)=>setAdjust((a)=>({ ...a, [e.id]: { ...(a[e.id]||{}), incentive: ev.target.value } }))}
                      className="h-7 w-20 text-right font-mono"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
